import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APP_PLATFORM, DEFAULT_SLOT_STORE_TEMPLATE, MAX_SLOT } from "../../config";
import { EmptySlotOption, Platform, Slot, SlotStatus } from "../../models";
import { createSlot, deleteSlot, updateSlot as updateSlotApi, getAllSlot, updateSlotStatus } from "../../services/conditions.service";
import { RootState } from "../store";
import { fetchConditionsAndSlotsAsync, fetchSlotsAsync } from "./appSlice";
import { PageType } from "@reconvert/reconvert-utils";

interface SlotState {
  slots: Slot[];
  error: string | null;
  loading: boolean;
  canCreateSlot: boolean;
  creatingSlot: boolean;
  isButtonDisabled?: boolean;
  pageSlots: Record<string, any[]>;
}

const initialState: SlotState = {
  slots: [],
  error: null,
  loading: false,
  canCreateSlot: true,
  creatingSlot: false,
  isButtonDisabled: false,
  pageSlots: {},
};

export const createSlotAsync = createAsyncThunk(
  "slot/create",
  async (
    slotConfig: { slotNo?: number | null; copyFromSlotNo?: number | null; copyFromVariantId?: string | null },
    { getState },
  ) => {
    const state = getState() as RootState;
    const activePage = state.app.activePage;
    const platformStoreId = state.app.platformStoreId;
    const selectedConditionId = state.conditions.selectedConditionId;

    const data = {
      name: "Slot Name",
      conditionId: selectedConditionId,
      location: activePage,
      slotNo: slotConfig.slotNo,
      copyFromSlotNo: slotConfig.copyFromSlotNo,
      copyFromVariantId: slotConfig.copyFromVariantId,
    };

    const response = await createSlot(data);

    if (!response.success) {
      throw new Error(response.message || "Error in creating slot");
    }

    response.platformStoreId = platformStoreId;

    return response;
  },
);

export const updateSlotAsync = createAsyncThunk("slot/update", async (data: { id: string; body: any }) => {
  const response = await updateSlotApi(data.id, data.body);

  if (!response.success) {
    throw new Error(response.message || "Error in updating slot");
  }

  return {
    ...response,
    ...data,
  };
});

export const updateSlotStatusAsync = createAsyncThunk("slots/updateSlotStatus", async (data: { id: string; body: any }) => {
  const response = await updateSlotStatus(data.body);
 
  if (!response.success) {
    throw new Error(response.message || "Error in updating slot other");
  }

  return {
    ...response,
    data,
  };
});

export const deleteSlotAsync = createAsyncThunk("slot/delete", async (slotNo: number, { getState }) => {
  const state = getState() as RootState;
  const activePage = state.app.activePage;
  const response = await deleteSlot(slotNo, activePage!.toString());

  if (!response.success) {
    throw new Error(response.message || "Error in deleting slot");
  }

  return { ...response, slotNo, activePage, platformStoreId: state.app.platformStoreId };
});

export const getSlotsForPageAsync = createAsyncThunk("slot/getSlotsForPage", async (pageType: PageType) => {
  const response = await getAllSlot(pageType);
  
  return {
    pageType,
    slots: response || []
  };
});

const checkIfCanCreateSlotState = (slots: Slot[], activePage: PageType, platformStoreId: string) => {
  let canCreateSlot = slots.length < MAX_SLOT;

  if (platformStoreId === DEFAULT_SLOT_STORE_TEMPLATE) {
    return canCreateSlot;
  }

  if (
    activePage === PageType.POST_PURCHASE_PAGE1 ||
    activePage === PageType.POST_PURCHASE_PAGE2 ||
    activePage === PageType.PRE_PURCHASE_CART_DRAWER_PAGE ||
    (APP_PLATFORM === Platform.Wix && activePage === PageType.CHECKOUT)
  ) {
    canCreateSlot = false;
  }

  return canCreateSlot;
};

const slotSlice = createSlice({
  name: "slots",
  initialState,
  reducers: {
    setCreatingSlot: (state, action: PayloadAction<boolean>) => {
      state.creatingSlot = action.payload;
    },
    updateSlot: (
      state,
      action: PayloadAction<{ id: string; name?: string; emptySlotOption?: EmptySlotOption; slotStatus?: SlotStatus }>,
    ) => {
      const slot = state.slots.find((slot: Slot) => slot.id === action.payload.id);

      if (slot) {
        slot.name = action.payload?.name || slot.name;
        slot.emptySlotOption = action.payload?.emptySlotOption || slot.emptySlotOption;
        slot.slotStatus = action.payload?.slotStatus || slot.slotStatus;
      }
    },
    updateSlotABTestingValue: (state, action: PayloadAction<{ id: string; isABTestingEnabled: boolean }[]>) => {
      return {
        ...state,
        slots: state.slots.map(slot => {
          const updateSlot = action.payload.find(u => u.id === slot.id);

          return updateSlot
            ? {
                ...slot,
                isABTestingEnabled: updateSlot.isABTestingEnabled,
              }
            : slot;
        }),
      };
    },
    addSlot: (
      state,
      action: PayloadAction<{ slot: Partial<Slot> & { id: string; slotNo: number }; activePage?: PageType; platformStoreId?: string }>,
    ) => {
      const { slot: slotPayload, activePage, platformStoreId } = action.payload;
      const slot = { ...slotPayload, id: slotPayload.id, _id: slotPayload._id ?? slotPayload.id } as Slot;

      if (!state.slots.some((s) => s.id === slot.id)) {
        state.slots.push(slot as Slot);
        state.slots.sort((a, b) => a.slotNo - b.slotNo);

        if (activePage != null && platformStoreId != null) {
          state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
        }
      }
    },
    removeSlot: (
      state,
      action: PayloadAction<{ slotNo: number; activePage?: PageType; platformStoreId?: string }>,
    ) => {
      const { slotNo, activePage, platformStoreId } = action.payload;

      state.slots = state.slots.filter((s) => s.slotNo !== slotNo);

      if (activePage != null && platformStoreId != null) {
        state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchConditionsAndSlotsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(fetchConditionsAndSlotsAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.slots = (action.payload.slots || []).map((slot: any) => ({ ...slot, id: slot._id }));
        state.slots.sort((a: any, b: any) => a.slotNo - b.slotNo);

        const activePage = action.payload.activePage;
        const platformStoreId = action.payload.platformStoreId;

        state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
        state.loading = false;
      })
      .addCase(fetchConditionsAndSlotsAsync.rejected, (state, error) => {
        state.error = error?.error?.message || "";
        state.loading = false;
      })
      .addCase(fetchSlotsAsync.fulfilled, (state, action: PayloadAction<any>) => {
        state.slots = (action.payload.slots || []).map((slot: any) => ({ ...slot, id: slot._id }));
        state.slots.sort((a: any, b: any) => a.slotNo - b.slotNo);

        const activePage = action.payload.activePage;
        const platformStoreId = action.payload.platformStoreId;

        state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
        state.loading = false;
      })
      .addCase(fetchSlotsAsync.rejected, (state, error) => {
        state.error = error?.error?.message || "";
        state.loading = false;
      })
      .addCase(createSlotAsync.fulfilled, (state, action: PayloadAction<any>) => {
        const activePage = action.payload.data.location;
        const platformStoreId = action.payload.data.platformStoreId;

        if (!state.slots.some(a => a.id === action.payload.data.id)) {
          state.slots = [...state.slots, action.payload.data];
          state.slots.sort((a: any, b: any) => a.slotNo - b.slotNo);
          state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
        }

        state.creatingSlot = false;
        state.isButtonDisabled = true;
      })
      .addCase(createSlotAsync.rejected, (state, error) => {
        state.creatingSlot = false;
      })
      .addCase(updateSlotAsync.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload.success) {
          const slotIndex = state.slots.findIndex((slot: any) => slot.id === action.payload.data.id);

          if (slotIndex > -1) {
            state.slots[slotIndex] = {
              ...state.slots[slotIndex],
              ...action.payload.data,
            };
          }
        }
      })
      .addCase(updateSlotStatusAsync.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload.success) {
          const slotIndex = state.slots.findIndex((slot: any) => slot.id === action.payload.data.id);
          
          if (slotIndex > -1) {
            state.slots[slotIndex] = {
              ...state.slots[slotIndex],
              slotStatus: action.payload.data.body.updateStatus,
            };
          }
        }
      })
      .addCase(deleteSlotAsync.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload.success) {
          const activePage = action.payload.activePage;
          const platformStoreId = action.payload.platformStoreId;

          state.slots = state.slots.filter((slot: any) => slot.slotNo !== action.payload.slotNo);
          state.canCreateSlot = checkIfCanCreateSlotState(state.slots, activePage, platformStoreId);
        }
      })
      .addCase(getSlotsForPageAsync.fulfilled, (state, action) => {
        state.pageSlots[action.payload.pageType] = action.payload.slots;
        console.log('Updated pageSlots state:', state.pageSlots);
      })
      .addCase(getSlotsForPageAsync.rejected, (state, error) => {
        console.log("slot/getSlotsForPageAsync", { error });
      });
  },
});

export const selectSlotsState = (state: RootState) => {
  return state.slots;
};

export const { setCreatingSlot, updateSlot, updateSlotABTestingValue, addSlot, removeSlot } = slotSlice.actions;

export default slotSlice.reducer;
